<template>
  <div class="auth-shell">
    <div class="auth-shell__mobile-logo lg:hidden">
      <img
        src="@/assets/images/logo-tc-black.svg"
        alt="ShopiMind"
      >
    </div>

    <div
      v-if="isLoadingForm"
      class="auth-shell__loader"
    >
      <i class="pi pi-spin pi-spinner" />
    </div>

    <header class="auth-shell__header">
      <h1 class="auth-shell__title">
        {{ t('login.welcome') }}
      </h1>
      <p class="auth-shell__intro">
        {{ t('login.loginInfo') }}
      </p>
    </header>

    <form
      class="auth-shell__body"
      data-test-id="form"
      @submit.prevent="checkForm"
    >
      <div class="auth-shell__field auth-shell__field--full">
        <BaseTextInput
          v-model="email"
          input-type="email"
          field-id="email"
          label="login.email"
          input-style="width: 100%;"
        />
      </div>

      <div class="auth-shell__field auth-shell__field--full">
        <BaseTextInput
          v-model="password"
          type="password"
          input-type="password"
          field-id="password"
          label="login.password"
          input-style="width: 100%;"
        />
      </div>

      <div class="auth-shell__row">
        <div class="auth-shell__check">
          <Checkbox
            id="rememberMe"
            v-model="checked"
            binary
          />
          <label for="rememberMe">{{ t('login.rememberMe') }}</label>
        </div>
        <router-link
          class="auth-shell__link"
          :to="{ name: 'user.forgot-password' }"
        >
          Mot de passe oublié ?
        </router-link>
      </div>

      <SpmButton
        :label="t('login.submit')"
        type="submit"
        style="width: 100%"
        class="flex p-button p-button-success auth-shell__btn"
      />

      <p class="auth-shell__foot">
        {{ t('login.registerQuestion') }}
        <router-link
          :to="{ name: 'user.registration-speed' }"
          class="auth-shell__link auth-shell__link--bold"
        >
          {{ t('login.register') }}
        </router-link>
      </p>
    </form>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, onBeforeMount, onMounted, onUnmounted, ref, watch,
} from 'vue';
import Checkbox from 'primevue/checkbox';
import { useI18n } from 'vue-i18n';

import {
  getIsAuthenticated, Login, Me, setPermissionsString, SwitchActiveShop, UserState,
} from '@/composables/User';
import { useStore } from '@/store';
import { showToastError } from '@/helpers';
import { NavigationFailure, useRouter } from 'vue-router';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import BaseTextInput from '@/components/fields/BaseTextInput.vue';
import { getConfigurationKey } from '@/composables/configs/configuration';

export default defineComponent({
  name: 'Login',
  components: {
    BaseTextInput,
    Checkbox,
    SpmButton,
  },

  setup() {
    const checked = ref(false);
    const email = ref('');
    const password = ref('');
    const isEmailNotValid = ref(false);
    const isPasswordNotValid = ref(false);
    const store = useStore();
    const { t } = useI18n();
    const router = useRouter();

    const isLoadingForm = ref(false);

    const checkForm = async (): Promise<void|NavigationFailure|undefined|null> => {
      isLoadingForm.value = true;
      isEmailNotValid.value = !email.value;
      isPasswordNotValid.value = !password.value;

      if (isEmailNotValid.value || isPasswordNotValid.value) {
        isLoadingForm.value = false;
        return null;
      }

      try {
        await Login({ username: email.value, password: password.value }, checked.value);
        await Me(false);

        if (UserState.user.shops.length === 0) {
          isLoadingForm.value = false;
          return showToastError(t('user.noActiveShop'));
        }

        if (UserState.user.shops.length === 1) {
          await SwitchActiveShop(UserState.user.shops[0].id);
          store.commit('liveEditor/resetWebsiteContents');
          isLoadingForm.value = false;
          return router.push({ name: 'home' });
        }

        isLoadingForm.value = false;
        return router.push({ name: 'choose-shop' });
      } catch (error: any) {
        isLoadingForm.value = false;
        if (error?.message === 'no_active_shop') {
          return showToastError(t('user.noActiveShop'));
        }
        return showToastError(t('user.wrongCredentials'));
      }
    };

    watch(email, (value: string) => {
      isEmailNotValid.value = !value;
    });

    watch(password, (value: string) => {
      isPasswordNotValid.value = !value;
    });

    onMounted(() => {
      document.body.classList.add('no_expand');
    });

    onBeforeMount(async () => {
      const isAuthenticated = getIsAuthenticated();
      if (isAuthenticated) {
        return router.push({ name: 'home' });
      }
      document.body.classList.add('no_expand');
      return true;
    });

    onUnmounted(() => {
      document.body.classList.remove('no_expand');
    });

    return {
      checked,
      email,
      password,
      isLoadingForm,
      t,
      checkForm,
    };
  },
});
</script>

<style scoped lang="scss">
.auth-shell {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 36rem;
  margin: 0 auto;
  padding: 0.5rem 0;

  &__mobile-logo {
    margin-bottom: 1.25rem;
    img { height: 28px; width: auto; }
  }

  &__loader {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    i { font-size: 1.5rem; color: #94c840; }
  }

  &__header {
    margin-bottom: 1.75rem;
  }

  &__title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 0.6rem;
    line-height: 1.25;
    letter-spacing: -0.01em;
  }

  &__intro {
    font-size: 0.92rem;
    color: #475569;
    margin: 0;
    line-height: 1.55;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: -0.25rem 0 0.25rem;
  }

  &__check {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    label {
      font-size: 0.8125rem;
      color: #475569;
      cursor: pointer;
      user-select: none;
    }

    :deep(.p-checkbox .p-checkbox-box) {
      border-radius: 4px;
      &.p-highlight {
        background: #94c840;
        border-color: #94c840;
      }
    }
  }

  &__link {
    font-size: 0.8125rem;
    color: #94c840;
    text-decoration: none;
    font-weight: 500;
    transition: color 0.15s;
    &:hover { color: #7eb52e; }

    &--bold { font-weight: 700; }
  }

  &__btn :deep(.p-button) {
    height: 2.75rem;
    border-radius: 0.5rem;
    font-weight: 600;
    font-size: 0.9375rem;
    background: #94c840;
    border-color: #94c840;
    transition: all 0.2s ease;

    &:hover {
      background: #84b738;
      border-color: #84b738;
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(148, 200, 64, 0.3);
    }
    &:active { transform: translateY(0); }
  }

  &__foot {
    text-align: center;
    margin: 0.5rem 0 0;
    font-size: 0.875rem;
    color: #6b7280;
  }
}

:deep(.field-wrapper .field-label) {
  font-size: 0.72rem !important;
  font-weight: 600 !important;
  color: #475569 !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.3rem !important;
}

:deep(.p-inputtext),
:deep(.p-password-input) {
  height: 2.3rem;
  border-radius: 0.5rem;
  border-color: #d1d5db;
  font-size: 0.9rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover { border-color: #94c840; }
  &:focus,
  &.p-focus {
    border-color: #94c840;
    box-shadow: 0 0 0 3px rgba(148, 200, 64, 0.18);
  }
}

:deep(.p-password) { width: 100%; }
</style>
